import { isAnyOf } from '@reduxjs/toolkit';

import { formsApi } from './formsApi';

export const FORM_ANSWERS_REDACTED = '[redacted: form answers]';

const isSubmitFormAction = isAnyOf(
  formsApi.endpoints.submitForm.matchPending,
  formsApi.endpoints.submitForm.matchFulfilled,
  formsApi.endpoints.submitForm.matchRejected
);

/**
 * Keeps answer text out of Sentry, whose redux enhancer records every action as a breadcrumb.
 *
 * A submission's actions carry the answers in more than one place: the request body in
 * `meta.arg.originalArgs`, and, on fulfilled and rejected, the raw axios response in
 * `meta.baseQueryMeta`, whose `config.data` is the same body as a JSON string. A rejected action's
 * payload holds the server's response body too. So rather than chase each copy, a submit action is
 * rebuilt from an allowlist: its type, request id and status, the form key, and the HTTP status.
 * The mutation's cache entry holds no arguments, but a failed one keeps the server's body, so
 * `redactFormAnswersState` covers Sentry's state snapshot.
 */
export const redactFormAnswersAction = <A>(action: A): A => {
  if (!isSubmitFormAction(action)) return action;
  const { requestId, requestStatus, arg } = action.meta;
  const status = (action.payload as { status?: unknown } | undefined)?.status;
  return {
    type: action.type,
    meta: {
      requestId,
      requestStatus,
      arg: { endpointName: arg.endpointName, formKey: arg.originalArgs.formKey },
      redacted: FORM_ANSWERS_REDACTED,
    },
    ...(typeof status === 'number' ? { payload: { status } } : {}),
  } as A;
};

/**
 * A failed submission's cache entry keeps the server's response body in `error.data`. The form
 * service answers with reason codes, but Sentry's state snapshot must not depend on that, so the
 * body is dropped and only the HTTP status kept. Returns the same state when nothing failed.
 */
export const redactFormAnswersState = <S extends Partial<Record<typeof formsApi.reducerPath, unknown>>>(
  state: S
): S => {
  const slice = state?.[formsApi.reducerPath] as ReturnType<typeof formsApi.reducer> | undefined;
  const failed = Object.entries(slice?.mutations ?? {}).filter(
    ([, entry]) => entry?.endpointName === 'submitForm' && entry.error
  );

  if (!slice || failed.length === 0) return state;

  const redacted = failed.map(([key, entry]) => [
    key,
    {
      ...entry,
      error: { status: (entry?.error as { status?: unknown } | undefined)?.status, data: FORM_ANSWERS_REDACTED },
    },
  ]);

  return {
    ...state,
    [formsApi.reducerPath]: { ...slice, mutations: { ...slice.mutations, ...Object.fromEntries(redacted) } },
  };
};
